import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    // DialogDescription,
    // DialogHeader,
    // DialogTitle,
} from 'components/ui/dialog';

import { useMemories, useDeleteMemory } from 'features/memory/hooks/use-memory';
import { Skeleton } from 'components/ui/skeleton';
import { Button } from 'components/ui/button';
import { Trash2, Brain } from 'lucide-react';
import { useToast } from 'hooks/use-toast';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import MemoryDeleteAlertDialog from 'features/memory/components/memory-alert-dialog';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
// import { useTranslation } from 'react-i18next';
// import { X } from 'lucide-react';


/**
 * Memory Dialog Component
 * 
 * A comprehensive settings dialog that provides access to different application settings
 * organized in tabs including General, Connectors, Notifications, Personalization, and Account.
 * 
 * Features:
 * - Tabbed navigation for different settings categories
 * - Responsive design with theme support
 * - Integration with existing UI components
 * - Proper accessibility and keyboard navigation
 * 
 * @param isOpen - Controls whether the dialog is displayed
 * @param setIsOpen - Callback function to control dialog visibility
 * @returns A settings dialog component
 */

interface MemoryDialogProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;

}

export const MemoryDialog = ({ isOpen, setIsOpen, }: MemoryDialogProps) => {
    const { t } = useTranslation();

    const { data, isLoading, refetch } = useMemories();

    const { mutate: deleteMemory, isPending: isDeleting } = useDeleteMemory();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [memoryToDelete, setMemoryToDelete] = useState<{ id: string; text: string } | null>(null);
    const { toast } = useToast();

    useEffect(() => {
        if (isOpen) {
            refetch();
        }
    }, [isOpen, refetch]);

    // const testMemories = [
    //     {
    //         id: "1",
    //         text: "Test Memory 1"
    //     },
    //     {
    //         id: "2",
    //         text: "Test Memory 2"
    //     },
    //     {
    //         id: "3",
    //         text: "Test Memory 3"
    //     },
    //     {
    //         id: "4",
    //         text: "Test Memory 4"
    //     },
    //     {
    //         id: "5",
    //         text: "Test Memory 5"
    //     },
    //     {
    //         id: "6",
    //         text: "Test Memory 6"
    //     },
    //     {
    //         id: "7",
    //         text: "Test Memory 7"
    //     },
    //     {
    //         id: "8",
    //         text: "Test Memory 8"
    //     },
    //     {
    //         id: "9",
    //         text: "Test Memory 9"
    //     },
    //     {
    //         id: "10",
    //         text: "Test Memory 10"
    //     },
    //     {
    //         id: "11",
    //         text: "Test Memory 11"
    //     },
    //     {
    //         id: "12",
    //         text: "Test Memory 12"
    //     },

    //     {
    //         id: "13",
    //         text: "Test Memory 13"
    //     },
    //     {
    //         id: "14",
    //         text: "Test Memory 14"
    //     }
    // ]

    // Static test data - 100 memories for testing




    // console.log("testMemories", testMemories, "isDeleting,isSuccess", isDeleting, isSuccess);

    const handleDeleteClick = (memory: { id: string; text: string }) => {
        setMemoryToDelete(memory);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (memoryToDelete) {
            deleteMemory(memoryToDelete.id, {
                onSuccess: () => {
                    toast({
                        variant: "success",
                        title: "Success",
                        description: "Memory deleted successfully"
                    });
                    setDeleteDialogOpen(false);
                    setMemoryToDelete(null);
                }
            });
        }
    };


    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="md:max-w-4xl  h-[85vh] md:h-[85vh] sm:h-[95vh] sm:max-w-[95vw] p-4 flex flex-col">

                <div className="space-y-2 pb-3 flex-shrink-0">
                    <h2 className="text-[16px] sm:text-[18px] font-semibold text-foreground leading-[24px] sm:leading-[28px]">
                        {t('SAVED_MEMORIES') || 'Saved Memories'}
                    </h2>
                    <p className="text-[13px] sm:text-[14px] leading-[20px] sm:leading-[22px] text-muted-foreground">
                        {t('SAVED_MEMORIES_DESCRIPTION') || 'EagleGPT tries to remember your recent chats, but it may forget things over time. Saved memories are never forgotten.'}
                    </p>


                </div>
                <div className='w-full border-t border-l border-r border-b-0 overflow-y-auto rounded-md bg-background mb-3 min-h-0'>
                    {isLoading ? (
                        <div className='space-y-2'>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <Skeleton key={index} className="w-full h-8" />
                            ))}
                        </div>
                    ) : data?.memories?.length && data?.memories?.length > 0 ? (
                        <>
                            {data?.memories?.map((memory, index) => (
                                <div key={memory?.id || index} className="px-4 py-2 border-b  flex items-center justify-between">
                                    <div className="text-sm font-light">{memory?.text}</div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteClick(memory as { id: string; text: string })}
                                        disabled={isDeleting}
                                    >
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <Trash2 className="h-4 w-4" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {t('DELETE') || 'Delete'}
                                            </TooltipContent>
                                        </Tooltip>
                                    </Button>
                                </div>
                            ))}

                        </>

                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-6">
                                <Brain className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-3">{t('NO_MEMORIES_YET') || 'No Memories Yet'}</h3>
                            <p className="text-muted-foreground max-w-md leading-relaxed">
                                {t('NO_MEMORIES_YET_DESCRIPTION') || 'You haven&apos;t saved any memories yet. Start a conversation with EagleGPT to create your first memory that will be remembered forever.'}
                            </p>
                            <div className="mt-6 px-4 py-2 bg-muted/50 rounded-lg border border-dashed border-muted-foreground/20">
                                <p className="text-sm text-muted-foreground">
                                    💡 <strong>{t('MEMORY_TIP') || 'Tip:'}</strong> {t('MEMORIES_HELP') || 'Memories help EagleGPT remember important information across conversations'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>




            </DialogContent>
            {/* Delete Confirmation Dialog */}
            <MemoryDeleteAlertDialog deleteDialogOpen={deleteDialogOpen} setDeleteDialogOpen={setDeleteDialogOpen} memoryToDelete={memoryToDelete} handleConfirmDelete={handleConfirmDelete} isDeleting={isDeleting} />

        </Dialog >

    );
};
